import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
} from "reactstrap";
import { withTranslation } from "react-i18next";
import moment from "moment";
import Page from "components/Page";
import Loader from "components/Loader";
import Empty from "components/Empty";
import Table from "components/table";
import {
  getPolicyFolderById,
  deletePolicyFolder,
  addPolicyAssignee,
  exportPolicyVersion,
} from "repositories/office-policy-management-repository";
import { useQuery } from "react-query";
import {
  decodeId,
  encodeId,
  cacheSideBarActive,
  formatCalendarDate,
  formatLocalDateTime,
} from "utils";
import constants, { officePolicyAssignmentStatus } from "../../../../constants";
import usePolicyDocumentListing, {
  sortingVariablesForPoliciesListing,
} from "accountOwner/pages/OfficePolicyManagement/hooks/usePolicyDocumentListing";
import PolicyDocumentListingFilters from "accountOwner/pages/OfficePolicyManagement/Filters/PolicyDocumentListingFilters";
import SearchBar from "accountOwner/pages/OfficePolicyManagement/Filters/Searchbar";
import DeleteConfirmationModal from "components/DeleteConfirmationModal";
import InviteesPopup from "accountOwner/pages/EventDetails/components/Modals/InviteesPopup";
import sortIcon from "assets/images/mir-ico-sort.svg";
import styles from "./../OfficePolicyManagement.module.scss";
import "./../OfficePolicyManagement.scss";
import toast from "react-hot-toast";
import PolicyAnalyticsModal from "./PolicyAnalyticsModal";
import SelectEmployeeForPolicyVersionModal from "../../AddEvent/components/SelectEmployeeForPolicyVersionModal";
import Text from "components/Text";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import { useComplianceTableRowClick } from "accountOwner/pages/OfficePolicyManagement/hooks/useComplianceTableRowClick";
export const getPolicyStatusClass = (statusLabel) => {
  const classes = {
    Pending: styles.Pending,
    Cancelled: styles.Cancelled,
    Started: styles.Started,
    Accepted: styles.Accepted,
    Rejected: styles.Rejected,
    "In Progress": styles.InProgress,
    Completed: styles.Completed,
    Draft: styles.Cancelled,
    [officePolicyAssignmentStatus.IN_PROGRESS]: styles.InProgress,
    [officePolicyAssignmentStatus.PENDING]: styles.Pending,
    [officePolicyAssignmentStatus.ACCEPTED]: styles.Accepted,
    [officePolicyAssignmentStatus.STARTED]: styles.Started,
    [officePolicyAssignmentStatus.COMPLETED]: styles.Completed,
    [officePolicyAssignmentStatus.REJECTED]: styles.Rejected,
    [officePolicyAssignmentStatus.CANCELLED]: styles.Cancelled,
    [officePolicyAssignmentStatus.DRAFT]: styles.Cancelled,
  };
  return `${styles.task_management_card_btn} ${classes[statusLabel] || ""}`;
};

/** Map staff list (e.g. ["BaleV Len", "Brandon Smith"]) to shape InviteesPopup expects. */
function normalizeStaffListForInviteesPopup(rawList) {
  if (!Array.isArray(rawList) || rawList.length === 0) return [];
  return rawList.map((item, index) => {
    const fullName =
      typeof item === "string"
        ? item
        : (item?.firstName + " " + item?.lastName ??
          item?.user?.fullName ??
          item?.fullName ??
          item?.name ??
          "");
    return {
      id: item?.id ?? index,
      user: { fullName: fullName || "—", profilePic: item?.profilePic },
    };
  });
}

/** Staff Assigned cell: All | single name | "X Staff Members" link opening InviteesPopup. */
function StaffAssignedCell({ row, t, onViewMoreStaff }) {
  const assignees = useMemo(() => {
    const raw =
      row?.assignedStaff ??
      row?.assignees ??
      row?.Assignees ??
      row?.staffAssigned ??
      [];
    if (raw === "All" || (typeof raw === "string" && raw.trim() === "All"))
      return "All";
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string" && raw.trim()) return [raw];
    return [];
  }, [row]);

  if (assignees === "All") {
    return (
      <span className={styles["table-text"]}>{t("notes.allFilters")}</span>
    );
  }
  if (!assignees?.length) {
    return <span className={styles["table-text"]}>—</span>;
  }
  if (assignees.length === 1) {
    const firstName =
      typeof assignees[0] === "string"
        ? assignees[0]
        : ((assignees[0]?.firstName || "") +
            " " +
            (assignees[0]?.lastName || "") ?? "");
    return <span className={styles["table-text"]}>{firstName || "—"}</span>;
  }
  return (
    <button
      type="button"
      className="link-btn p-0 border-0 bg-transparent text-primary"
      onClick={(e) => onViewMoreStaff(e, assignees)}
    >
      {t("officePolicyManagement.staffMembersCount", {
        count: assignees.length,
      })}
    </button>
  );
}

function PolicyDocumentListing({ t }) {
  const { officePolicyFolderId } = useParams();
  const history = useHistory();
  const folderId = decodeId(officePolicyFolderId);
  const openRef = useRef(null);
  const { effectiveOwnerIdString, getIdToCheckOwnerChange, effectiveOwnerId } =
    useAccountOwnerCommon();
  const [isSidebarActive, setSidebarActive] = useState(
    cacheSideBarActive(constants.sidebar?.cache?.officePoliciesIsSidebarActive),
  );
  const [globalDropdownOpen, setGlobalDropdownOpen] = useState(false);
  const globalDropdownRef = useRef(null);
  const containerRef = useRef(null);
  const [staffPopup, setStaffPopup] = useState({ isOpen: false, list: [] });
  const [invitePopupPosition, setInvitePopupPosition] = useState({
    top: 0,
    right: 0,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);
  const [addAssigneePolicy, setAddAssigneePolicy] = useState(null);
  const [isPolicyAnalyticsModalOpen, setIsPolicyAnalyticsModalOpen] =
    useState(null);
  const [addAssigneeLoading, setAddAssigneeLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { data: folderData } = useQuery(
    ["/PolicyFolder", folderId],
    () => getPolicyFolderById(folderId),
    { enabled: !!folderId },
  );

  const {
    isLoading,
    items,
    inputValue,
    handleSearchTerm,
    officeFilter,
    setOfficeFilter,
    staffFilter,
    setStaffFilter,
    statusFilter,
    setStatusFilter,
    dateRange,
    setDateRange,
    isFilterApply,
    handleApplyFilters,
    resetFilter,
    accountOwnerId: ownerId,
    isStaff,
    refetch,
    appliedOfficeFilter,
    appliedStaffFilter,
    appliedStatusFilter,
    appliedDateRange,
    searchValue,
    sortBy,
    setSortBy,
    isDescending,
    setIsDescending,
  } = usePolicyDocumentListing(folderId);
  useEffect(() => {
    if (
      (folderData?.ownerId && ownerId && folderData?.ownerId !== ownerId) ||
      getIdToCheckOwnerChange()
    ) {
      history.push(
        constants.routes.officePolicyManagements.officePolicyManagement,
      );
    }
  }, [folderData?.ownerId, ownerId, effectiveOwnerId]);

  const addAssigneePolicyId =
    addAssigneePolicy?.id ?? addAssigneePolicy?.policyId;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        globalDropdownRef.current &&
        !globalDropdownRef.current.contains(e.target)
      ) {
        setGlobalDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSidebarToggle = () => {
    const next = !isSidebarActive;
    setSidebarActive(next);
    if (constants.sidebar?.cache?.officePoliciesIsSidebarActive) {
      localStorage.setItem(
        constants.sidebar.cache.officePoliciesIsSidebarActive,
        next,
      );
    }
  };

  const handleExport = useCallback(async () => {
    setGlobalDropdownOpen(false);
    if (!folderId) return;
    const fromDate = appliedDateRange?.fromDate ?? null;
    const toDate = appliedDateRange?.toDate ?? null;
    const dateUploadedFrom =
      fromDate &&
      (fromDate instanceof Date
        ? moment(fromDate).format("YYYY-MM-DD")
        : fromDate);
    const dateUploadedTo =
      toDate &&
      (toDate instanceof Date ? moment(toDate).format("YYYY-MM-DD") : toDate);
    setExportLoading(true);
    try {
      const res = await exportPolicyVersion(folderId, {
        officeIds: appliedOfficeFilter || [],
        statuses: appliedStatusFilter || [],
        searchTerm: searchValue || "",
        userIds: appliedStaffFilter || [],
        dateUploadedFrom: dateUploadedFrom || null,
        dateUploadedTo: dateUploadedTo || null,
        sortBy: sortBy ?? null,
        sortDirection: isDescending ? 1 : 2,
      });

      if (res?.message) {
        toast.success(res?.message);
      }
    } catch (err) {
      toast.error(err?.message || t("officePolicyManagement.exportFailed"));
    } finally {
      setExportLoading(false);
    }
  }, [
    folderId,
    appliedOfficeFilter,
    appliedStaffFilter,
    appliedStatusFilter,
    appliedDateRange,
    searchValue,
    sortBy,
    isDescending,
    t,
  ]);

  const handleViewMoreStaff = useCallback((e, staffList) => {
    e.stopPropagation();
    let top = 0;
    let right = 0;
    if (containerRef.current && e.currentTarget) {
      const btnRect = e.currentTarget.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      const popupEstimatedWidth = 280;

      // Position popup exactly over the clicked element
      // Calculate top position: button's top relative to container
      top = btnRect.top + 70 - containerRect.top;

      // Calculate right position: distance from container's right edge to button's right edge
      right = containerRect.right - btnRect.right;

      // Center horizontally: adjust right to center the popup over the button
      const buttonWidth = btnRect.width;
      right = right + buttonWidth / 2 - popupEstimatedWidth / 2;

      // Ensure popup stays within container bounds
      if (right < 10) {
        right = 10;
      }
      if (right + popupEstimatedWidth > containerRect.width) {
        right = containerRect.width - popupEstimatedWidth - 10;
      }
    }
    setInvitePopupPosition({ top, right });
    setStaffPopup({
      isOpen: true,
      list: normalizeStaffListForInviteesPopup(
        Array.isArray(staffList) ? staffList : [],
      ),
    });
  }, []);

  const handleInvitePopupToggle = useCallback((e) => {
    e?.stopPropagation?.();
    setStaffPopup((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleAddAssigneeOpen = useCallback((row) => {
    setAddAssigneePolicy(row);
  }, []);

  const handleAddAssigneeClose = useCallback(() => {
    setAddAssigneePolicy(null);
  }, []);

  const handleAddAssigneeConfirm = useCallback(
    (list) => {
      const policyId = addAssigneePolicy?.id ?? addAssigneePolicy?.policyId;
      if (policyId == null) {
        setAddAssigneePolicy(null);
        return;
      }
      if (list.length === 0) {
        toast.error(t("officePolicyManagement.pleaseSelectAtLeastOneAssignee"));
        return;
      }

      const assigneeIds = (list || []).reduce((acc, curr) => {
        if (curr.offices.length) {
          const values = curr.offices[0];
          acc.push({
            UserId: curr.userId,
            OfficeId: values.officeId,
            IsOwner: curr.isOwner,
          });
        }
        return acc;
      }, []);
      if (assigneeIds.length === 0) {
        setAddAssigneePolicy(null);
        return;
      }
      setAddAssigneeLoading(true);
      addPolicyAssignee(policyId, { Assignees: assigneeIds })
        .then((res) => {
          refetch();
          toast.success(res?.message);
          setAddAssigneePolicy(null);
        })
        .catch((err) => {
          toast.error(err?.message ?? "Failed to add assignees.");
        })
        .finally(() => {
          setAddAssigneeLoading(false);
        });
    },
    [addAssigneePolicy, refetch],
  );

  const handleView = useCallback(
    (row) => {
      const viewPolicyRoute =
        constants.routes.officePolicyManagements
          .officePolicyManagementOwnerDetailView;
      const path = viewPolicyRoute.replace(
        ":officePolicyFolderId",
        officePolicyFolderId || encodeId(folderId),
      );
      const policyId = row?.id ?? row?.policyId;
      const url =
        policyId != null
          ? `${path}?policyId=${encodeId(policyId)}&policyView=1${effectiveOwnerIdString ? `&${effectiveOwnerIdString}` : ""}`
          : `${path}${effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : ""}`;
      history.push(url);
    },
    [history, officePolicyFolderId, folderId, effectiveOwnerIdString],
  );

  const { preventRowNavigation, runDropdownAction, rowEvents } =
    useComplianceTableRowClick(handleView);

  const handleEdit = useCallback(
    (row) => {
      const policyId = row?.id ?? row?.policyId;
      if (policyId == null) return;
      const editRoute =
        constants.routes.officePolicyManagements
          .officePolicyManagementEditPolicy;
      const url = editRoute
        .replace(
          ":officePolicyFolderId",
          officePolicyFolderId || encodeId(folderId),
        )
        .replace(":policyId", encodeId(policyId));
      history.push({
        pathname: url,
        search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
      });
    },
    [history, officePolicyFolderId, folderId, effectiveOwnerIdString],
  );

  const handleShowAnalytics = useCallback(
    (row) => {
      setIsPolicyAnalyticsModalOpen(row);
    },
    [t],
  );

  const closeDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setPolicyToDelete(null);
  }, []);

  const handleDeleteRow = useCallback((row) => {
    setPolicyToDelete(row);
    setShowDeleteModal(true);
  }, []);

  const confirmDeletePolicyVersion = useCallback(async () => {
    const policyId = policyToDelete?.id ?? policyToDelete?.policyId;
    if (policyId == null || folderId == null || ownerId == null) {
      closeDeleteModal();
      return;
    }
    setDeleteLoading(true);
    try {
      const res = await deletePolicyFolder({
        OwnerId: ownerId,
        Items: [{ PolicyFolderId: folderId, PolicyId: policyId }],
      });
      refetch();
      if (res?.message) toast.success(res.message);
      closeDeleteModal();
    } catch (err) {
      toast.error(err?.message);
    } finally {
      setDeleteLoading(false);
    }
  }, [policyToDelete, closeDeleteModal, refetch, folderId, ownerId]);

  /** Go to folder listing in one step; avoid history.goBack() so stacked routes (view/edit/add) do not require multiple clicks. */
  const goBack = useCallback(() => {
    history.replace({
      pathname: constants.routes.officePolicyManagements.officePolicyManagement,
      search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
    });
  }, [history, effectiveOwnerIdString]);

  const sortAndAscDescSetter = useCallback(
    (assignSort) => {
      let newIsDescending = true;
      if (sortBy === assignSort) {
        newIsDescending = !isDescending;
      }
      setSortBy(assignSort);
      setIsDescending(newIsDescending);
    },
    [sortBy, isDescending, setSortBy, setIsDescending],
  );
  const columns = useMemo(
    () => [
      {
        attrs: { datatitle: t("officePolicyManagement.documentName") },
        dataField: "documentName",
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1">
              <button
                className={
                  "reset-btn flex-shrink-null " + styles["th-btn-wrapper"]
                }
                onClick={() => {
                  sortAndAscDescSetter(
                    sortingVariablesForPoliciesListing.DocumentName,
                  );
                }}
              >
                <span>{t("officePolicyManagement.documentName")}</span>
                <img
                  src={sortIcon}
                  alt="sortIcon"
                  style={{
                    transform:
                      isDescending &&
                      sortBy === sortingVariablesForPoliciesListing.DocumentName
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          );
        },
        formatter: (cell, row) => (
          <span
            className={`${styles["table-text"]} ${styles["table-text-inherit"]}`}
          >
            {String(row?.documentName ?? "").trim() || "—"}
          </span>
        ),
        sort: false,
      },
      {
        attrs: { datatitle: t("officePolicyManagement.notes") },
        dataField: "notes",
        text: t("officePolicyManagement.notes"),
        formatter: (cell, row) => (
          <span className={styles["table-text"]}>
            {String(row?.notes ?? "").trim() || "—"}
          </span>
        ),
        sort: false,
      },
      {
        attrs: { datatitle: t("officePolicyManagement.versionNo") },
        dataField: "versionNo",
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1">
              <button
                className={
                  "reset-btn flex-shrink-null " + styles["th-btn-wrapper"]
                }
                onClick={() => {
                  sortAndAscDescSetter(
                    sortingVariablesForPoliciesListing.VersionNumber,
                  );
                }}
              >
                <span>{t("officePolicyManagement.versionNo")}</span>
                <img
                  src={sortIcon}
                  alt="sortIcon"
                  style={{
                    transform:
                      isDescending &&
                      sortBy ===
                        sortingVariablesForPoliciesListing.VersionNumber
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          );
        },
        formatter: (cell, row) => (
          <span className={styles["table-text"]}>
            {String(row?.versionNumber ?? "").trim() || "—"}
          </span>
        ),
        sort: false,
      },
      {
        attrs: { datatitle: t("officePolicyManagement.uploadedBy") },
        dataField: "uploadedBy",
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1">
              <button
                className={
                  "reset-btn flex-shrink-null " + styles["th-btn-wrapper"]
                }
                onClick={() => {
                  sortAndAscDescSetter(
                    sortingVariablesForPoliciesListing.UploadedBy,
                  );
                }}
              >
                <span>{t("officePolicyManagement.uploadedBy")}</span>
                <img
                  src={sortIcon}
                  alt="sortIcon"
                  style={{
                    transform:
                      isDescending &&
                      sortBy === sortingVariablesForPoliciesListing.UploadedBy
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          );
        },
        formatter: (cell, row) => (
          <span className={styles["table-text"]}>
            {row?.uploadedBy ?? row?.uploadedByName ?? "—"}
          </span>
        ),
        sort: false,
      },
      {
        attrs: { datatitle: t("officePolicyManagement.dateUploaded") },
        dataField: "dateUploaded",
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1">
              <button
                className={
                  "reset-btn flex-shrink-null " + styles["th-btn-wrapper"]
                }
                onClick={() => {
                  sortAndAscDescSetter(
                    sortingVariablesForPoliciesListing.DateUploaded,
                  );
                }}
              >
                <span>{t("officePolicyManagement.dateUploaded")}</span>
                <img
                  src={sortIcon}
                  alt="sortIcon"
                  style={{
                    transform:
                      isDescending &&
                      sortBy === sortingVariablesForPoliciesListing.DateUploaded
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          );
        },
        formatter: (cell, row) => (
          <span className={styles["table-text"]}>
            {formatLocalDateTime(
              row?.dateUploaded ?? row?.createdAt,
              "MMM DD, YYYY",
            ) || "—"}
          </span>
        ),
        sort: false,
      },
      {
        attrs: { datatitle: t("officePolicyManagement.startDate") },
        dataField: "startDate",
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1">
              <button
                className={
                  "reset-btn flex-shrink-null " + styles["th-btn-wrapper"]
                }
                onClick={() => {
                  sortAndAscDescSetter(
                    sortingVariablesForPoliciesListing.StartDateDueDate,
                  );
                }}
              >
                <span>{t("officePolicyManagement.startDate")}</span>
                <img
                  src={sortIcon}
                  alt="sortIcon"
                  style={{
                    transform:
                      isDescending &&
                      sortBy ===
                        sortingVariablesForPoliciesListing.StartDateDueDate
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          );
        },
        formatter: (cell, row) => (
          <span className={styles["table-text"]}>
            {formatCalendarDate(row?.startDate, "MMM DD, YYYY") || "—"}
          </span>
        ),
        sort: false,
      },
      {
        attrs: { datatitle: t("officePolicyManagement.dueDate") },
        dataField: "dueDate",
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1">
              <button
                className={
                  "reset-btn flex-shrink-null " + styles["th-btn-wrapper"]
                }
                onClick={() => {
                  sortAndAscDescSetter(
                    sortingVariablesForPoliciesListing.StartDateDueDate,
                  );
                }}
              >
                <span>{t("officePolicyManagement.dueDate")}</span>
                <img
                  src={sortIcon}
                  alt="sortIcon"
                  style={{
                    transform:
                      isDescending &&
                      sortBy ===
                        sortingVariablesForPoliciesListing.StartDateDueDate
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          );
        },
        formatter: (cell, row) => (
          <span className={styles["table-text"]}>
            {formatCalendarDate(row?.dueDate, "MMM DD, YYYY") || "—"}
          </span>
        ),
        sort: false,
      },
      {
        attrs: { datatitle: t("officePolicyManagement.staffAssigned") },
        dataField: "staffAssigned",
        text: t("officePolicyManagement.staffAssigned"),
        formatter: (cell, row) => (
          <StaffAssignedCell
            row={row}
            t={t}
            onViewMoreStaff={handleViewMoreStaff}
          />
        ),
        sort: false,
      },
      {
        attrs: { datatitle: t("officePolicyManagement.status") },
        dataField: "status",
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1">
              <button
                className={
                  "reset-btn flex-shrink-null " + styles["th-btn-wrapper"]
                }
                onClick={() => {
                  sortAndAscDescSetter(
                    sortingVariablesForPoliciesListing.Status,
                  );
                }}
              >
                <span>{t("officePolicyManagement.status")}</span>
                <img
                  src={sortIcon}
                  alt="sortIcon"
                  style={{
                    transform:
                      isDescending &&
                      sortBy === sortingVariablesForPoliciesListing.Status
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          );
        },
        formatter: (cell, row) => {
          return (
            <span className={getPolicyStatusClass(row?.status)}>
              {row?.status || "—"}
            </span>
          );
        },
        sort: false,
      },
      {
        attrs: { datatitle: t("officePolicyManagement.actions") },
        dataField: "actions",
        text: t("officePolicyManagement.actions"),
        formatter: (cell, row) => (
          <div
            className="actions-status-box"
            onMouseDown={preventRowNavigation}
            onClick={preventRowNavigation}
          >
            <UncontrolledDropdown
              tag="div"
              className={`interactive-element d-inline-block ${styles.policy_row_actions_dropdown}`}
            >
              <DropdownToggle
                tag="div"
                className="cursor-pointer p-1"
                caret={false}
                onMouseDown={preventRowNavigation}
                onClick={preventRowNavigation}
              >
                <img
                  src={require("assets/images/dots-icon.svg").default}
                  alt="actions"
                />
              </DropdownToggle>
              <DropdownMenu
                end
                container={openRef}
                onMouseDown={preventRowNavigation}
              >
                <DropdownItem
                  onMouseDown={preventRowNavigation}
                  onClick={(e) => runDropdownAction(e, () => handleView(row))}
                >
                  <Text className={"office-dropdown-item"}>
                    {t("officePolicyManagement.view")}
                  </Text>
                </DropdownItem>
                <DropdownItem
                  onMouseDown={preventRowNavigation}
                  onClick={(e) => runDropdownAction(e, () => handleEdit(row))}
                >
                  <Text className={"office-dropdown-item"}>
                    {t("officePolicyManagement.edit")}
                  </Text>
                </DropdownItem>
                {row?.status === "Draft" && (
                  <DropdownItem
                    onMouseDown={preventRowNavigation}
                    onClick={(e) =>
                      runDropdownAction(e, () => handleAddAssigneeOpen(row))
                    }
                  >
                    <Text className={"office-dropdown-item"}>
                      {t("officePolicyManagement.addAssignee")}
                    </Text>
                  </DropdownItem>
                )}
                <DropdownItem
                  onMouseDown={preventRowNavigation}
                  onClick={(e) =>
                    runDropdownAction(e, () => handleShowAnalytics(row))
                  }
                >
                  <Text className={"office-dropdown-item"}>
                    {t("officePolicyManagement.showAnalytics")}
                  </Text>
                </DropdownItem>
                <DropdownItem
                  onMouseDown={preventRowNavigation}
                  onClick={(e) =>
                    runDropdownAction(e, () => handleDeleteRow(row))
                  }
                >
                  <Text className={"office-dropdown-item"}>
                    {t("officePolicyManagement.delete")}
                  </Text>
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        ),
        sort: false,
      },
    ],
    [
      t,
      handleViewMoreStaff,
      handleDeleteRow,
      handleAddAssigneeOpen,
      sortAndAscDescSetter,
      sortBy,
      isDescending,
      handleView,
      handleEdit,
      handleShowAnalytics,
      preventRowNavigation,
      runDropdownAction,
    ],
  );

  const emptyMessage = isFilterApply
    ? t("officePolicyManagement.noDataAvailableForSelection")
    : t("officePolicyManagement.noVersionsAvailableForSelection");

  const actionButton = (
    <div
      className={`d-flex align-items-center justify-content-end ${styles.policy_header_actions} ${styles["policy-hearder-box"]}`}
    >
      <div
        className={`${styles.policy_actions_dropdown} cursor-pointer`}
        ref={globalDropdownRef}
        onClick={() => setGlobalDropdownOpen((p) => !p)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setGlobalDropdownOpen((p) => !p);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <Dropdown isOpen={globalDropdownOpen} toggle={() => {}}>
          <DropdownToggle caret={false} tag="div">
            <span className="ico">
              <img
                src={require("assets/images/dots-icon.svg").default}
                alt=""
              />
            </span>
          </DropdownToggle>
          <DropdownMenu end>
            <DropdownItem onClick={handleExport}>
              <span>{t("officePolicyManagement.export")}</span>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>
      <Link
        to={constants.routes.officePolicyManagements.officePolicyManagementAddPolicy.replace(
          ":officePolicyFolderId",
          officePolicyFolderId || "",
        )}
        className={`button button-round button-shadow ${styles.policy_add_btn}`}
        title={t("officePolicyManagement.addNewPolicyDocument")}
      >
        {t("officePolicyManagement.addNewPolicyDocument")}
      </Link>
    </div>
  );

  return (
    <Page
      onBack={goBack}
      className="scheduler-page"
      title={`${folderData?.title} - ${t("officePolicyManagement.policyDetails")}`}
      titleKey={null}
      actionButton={actionButton}
    >
      {(exportLoading || deleteLoading) && <Loader />}
      <DeleteConfirmationModal
        confirmDelete={showDeleteModal}
        closeDeleteModal={closeDeleteModal}
        confirmDeleteMessage={confirmDeletePolicyVersion}
        title={t("officePolicyManagement.deletePolicyVersion")}
        deleteMessage={t("officePolicyManagement.deletePolicyVersionMessage")}
        btnText={t("officePolicyManagement.deletePolicyVersionBtn")}
        isLoading={deleteLoading}
      />
      <div className="pt-0 position-relative" ref={containerRef}>
        <div className={styles.policy_listing_wrapper_card_box}>
          <div className="scheduler-tabs-main-wrapper">
            <PolicyDocumentListingFilters
              t={t}
              isSidebarActive={isSidebarActive}
              handleSidebarToggle={handleSidebarToggle}
              resetFilter={resetFilter}
              officeFilter={officeFilter}
              setOfficeFilter={setOfficeFilter}
              handleApplyFilters={handleApplyFilters}
              staffFilter={staffFilter}
              setStaffFilter={setStaffFilter}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              dateRange={dateRange}
              setDateRange={setDateRange}
              isFilterApply={isFilterApply}
              accountOwnerId={ownerId}
              isStaff={isStaff}
            />

            <div className={styles.compliance_table_wrapper}>
              <div className={styles.compliance_table_toolbar}>
                <SearchBar
                  searchValue={inputValue}
                  handleSearchTerm={handleSearchTerm}
                  t={t}
                  placeholder={
                    t("officePolicyManagement.searchByName") || "Search by Name"
                  }
                />
              </div>
              {isLoading && <Loader />}
              {!isLoading && (!items || items.length === 0) && (
                <Empty Message={emptyMessage} />
              )}
              <div
                className="actions-status-box"
                ref={openRef}
                onMouseDown={preventRowNavigation}
                onClick={preventRowNavigation}
              />
              {!isLoading && items?.length > 0 && (
                <div className={styles.compliance_table_scroll}>
                  <Table
                    key="policy-document-table"
                    keyField="id"
                    data={items}
                    columns={columns}
                    bordered={false}
                    disablePagination
                    tableClassName={styles.compliance_policy_table}
                    rowEvents={rowEvents}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {staffPopup.isOpen && staffPopup.list.length > 0 && (
        <InviteesPopup
          position={invitePopupPosition}
          handleInvitePopupToggle={handleInvitePopupToggle}
          list={staffPopup.list}
          onlyOfffice={false}
          isForEvent={true}
          isfromBusySlot={true}
          listClass={styles.compliance_invitees_popup_list}
        />
      )}
      {isPolicyAnalyticsModalOpen && (
        <PolicyAnalyticsModal
          isOpen={true}
          data={isPolicyAnalyticsModalOpen}
          onClose={() => setIsPolicyAnalyticsModalOpen(null)}
        />
      )}
      {addAssigneePolicy && (
        <SelectEmployeeForPolicyVersionModal
          t={t}
          issaveEmployeeModalOpen={!!addAssigneePolicy}
          setIsSaveEmployeeModalOpen={handleAddAssigneeClose}
          memberIds={[]}
          setMemberIds={handleAddAssigneeConfirm}
          dontCloseModal={true}
          policyFolderId={folderId}
          ownerId={ownerId}
          policyId={addAssigneePolicyId}
          confirmLoading={addAssigneeLoading}
          isManagerOnly={false}
          officeIds={
            folderData?.offices.map((o) => ({
              id: o.officeId,
              name: o.officeName,
            })) || []
          }
        />
      )}
    </Page>
  );
}

export default withTranslation()(PolicyDocumentListing);
